import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../utils/api'
import { ClipboardList, TrendingUp, MapPin, CheckCircle, Clock, XCircle, FileText } from 'lucide-react'

const STATUS_LABELS = {
  draft:            'مسودة',
  submitted:        'مقدَّم',
  'pending review': 'قيد المراجعة',
  documented:       'موثَّق',
  rejected:         'مرفوض',
}
const STATUS_COLOR = {
  draft:            'bg-stone-200 text-stone-600',
  submitted:        'bg-blue-100 text-blue-700',
  'pending review': 'bg-amber-100 text-amber-700',
  documented:       'bg-green-100 text-green-700',
  rejected:         'bg-red-100 text-red-700',
}
const STATUS_BAR = {
  draft:            'bg-stone-400',
  submitted:        'bg-blue-400',
  'pending review': 'bg-amber-400',
  documented:       'bg-green-500',
  rejected:         'bg-red-400',
}

function StatCard({ label, value, icon: Icon, color = 'text-primary-700', sub }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5 flex items-start gap-4">
      <div className={`p-2.5 rounded-xl bg-stone-100 ${color}`}>
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <div>
        <p className="text-2xl font-bold text-stone-800">{value ?? '—'}</p>
        <p className="text-sm text-stone-500 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-stone-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  )
}

function TrendBar({ day, count, max }) {
  const pct = max ? Math.round((count / max) * 100) : 0
  return (
    <div className="flex flex-col items-center gap-1" title={`${day}: ${count}`}>
      <span className="text-[10px] text-stone-500">{count}</span>
      <div className="w-5 bg-stone-100 rounded-full overflow-hidden" style={{ height: 60 }}>
        <div
          className="w-full bg-primary-400 rounded-full transition-all"
          style={{ height: `${pct}%`, marginTop: `${100 - pct}%` }}
        />
      </div>
      <span className="text-[9px] text-stone-400 rotate-45 origin-top-left mt-1">
        {day?.slice(5)}
      </span>
    </div>
  )
}

export default function Dashboard() {
  const [data,    setData]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState('')

  useEffect(() => {
    api.get('admin/dashboard')
      .then(r => setData(r.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return (
    <div className="flex items-center justify-center py-24 text-stone-400">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
    </div>
  )

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
  )

  if (!data) return null

  const documented = data.by_status?.find(r => r.status === 'documented')?.count || 0
  const pending    = data.by_status?.find(r => r.status === 'pending review')?.count || 0
  const rejected   = data.by_status?.find(r => r.status === 'rejected')?.count || 0
  const maxTrend   = Math.max(...(data.trend?.map(r => r.count) || [1]))

  return (
    <div dir="rtl" className="space-y-6">
      <h1 className="text-2xl font-bold text-stone-800">لوحة الإحصاء</h1>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="إجمالي الحالات"  value={data.total}     icon={ClipboardList} />
        <StatCard label="هذا الأسبوع"     value={data.this_week} icon={TrendingUp}   color="text-blue-600"  sub="آخر 7 أيام" />
        <StatCard label="موثَّق"           value={documented}     icon={CheckCircle}  color="text-green-600" />
        <StatCard label="قيد المراجعة"    value={pending}        icon={Clock}        color="text-amber-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status breakdown */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <h2 className="font-semibold text-stone-700 mb-4 flex items-center gap-2">
            <FileText size={15} className="text-primary-500" />
            توزيع الحالات حسب الحالة
          </h2>
          <div className="space-y-2.5">
            {(data.by_status || []).map(row => {
              const pct = data.total ? Math.round((row.count / data.total) * 100) : 0
              return (
                <div key={row.status}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[row.status] || 'bg-stone-100 text-stone-600'}`}>
                      {STATUS_LABELS[row.status] || row.status}
                    </span>
                    <span className="text-sm font-semibold text-stone-700">{row.count} <span className="text-stone-400 font-normal text-xs">({pct}%)</span></span>
                  </div>
                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${STATUS_BAR[row.status] || 'bg-stone-400'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Top regions */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <h2 className="font-semibold text-stone-700 mb-4 flex items-center gap-2">
            <MapPin size={15} className="text-primary-500" />
            أعلى المناطق
          </h2>
          <div className="space-y-2">
            {(data.by_region || []).slice(0, 8).map((row, i) => {
              const pct = data.total ? Math.round((row.count / data.total) * 100) : 0
              return (
                <div key={row.region} className="flex items-center gap-3">
                  <span className="text-xs text-stone-400 w-4 text-left">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-sm text-stone-700">{row.region}</span>
                      <span className="text-xs text-stone-500">{row.count}</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-400 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
            {(data.by_region || []).length === 0 && (
              <p className="text-stone-400 text-sm">لا توجد بيانات بعد</p>
            )}
          </div>
        </div>
      </div>

      {/* 14-day trend */}
      {(data.trend?.length > 0) && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <h2 className="font-semibold text-stone-700 mb-4 flex items-center gap-2">
            <TrendingUp size={15} className="text-primary-500" />
            الطلبات الواردة (آخر 14 يوماً)
          </h2>
          <div className="flex items-end gap-2 overflow-x-auto pb-2">
            {data.trend.map(row => (
              <TrendBar key={row.day} day={row.day} count={row.count} max={maxTrend} />
            ))}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="flex gap-3 flex-wrap">
        <Link
          to="/admin/cases"
          className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white text-sm px-4 py-2 rounded-xl transition-colors"
        >
          <ClipboardList size={14} />
          عرض الحالات
        </Link>
        <Link
          to="/admin/cases?status=pending+review"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-xl transition-colors"
        >
          <Clock size={14} />
          المراجعة المعلقة ({pending})
        </Link>
      </div>
    </div>
  )
}
